/**
 * The engineering floor.
 *
 * One SVG, composed in a fixed design width and scaled by its viewBox, so the arithmetic never has
 * to know the size of the viewport.
 *
 * **Where the animation lives.** Almost nowhere in React. Each entity is positioned with a
 * `transform`, and CSS transitions carry it from one position to the next — a bug leaving
 * containment for a workstation is a changed transform and a 1.6s ease, not a loop. Continuous
 * motion (wing beats, strides, hovering, drifting in the jar, the swatter) is CSS keyframes. React
 * re-renders only when the store's snapshot changes, which is when the backend said something
 * changed.
 *
 * That is the whole performance strategy, and it is also why `prefers-reduced-motion` is cheap to
 * honour: turning off transitions and keyframes leaves the scene correct and still.
 *
 * **The room's light follows the viewer's clock, not the page theme** (see scenery.ts). An earlier
 * daytime room lost the glass, the wings and the neon — everything that is light-on-dark — so the
 * daytime palettes in scenery.css keep the wall a mid-blue rather than white, and only the wall,
 * floor and sky change; the figures, cards and mosquitoes are the same at every hour.
 */

import { useSceneryChoice } from './useRoomScenery'
import { useI18n } from '../lib/i18n'
import { usePrefersReducedMotion } from '../lib/theme'
import { memo, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useScenery, type TimeOfDay } from './scenery'
import { HolidayDecor } from './Holiday'
import type { ActivityState, BugHuntSession, BugState } from '../lib/types'
import type { BugEntity, DeveloperEntity, GameState } from './store'
import {
  computeFloor, jarCell, assignJarCells, bugSlotAt, agentSlotAt, seedFrom, containmentBox, horizonFor,
  designHeightFor, roomHeightFor, wrapSpeech, isCrowded, CARD_WIDTH, DESIGN_WIDTH, JAR_CELLS, MAX_CLONES, cloneName, cloneSpotAt, type FloorSize, type Station,
} from './layout'
import { visualStateFor, isContained, isAtStation, urgency, type VisualBugState } from './visual'
import { BugCreature, Chair, Desk, DeveloperFigure, lookFor, type Pose } from './Figures'
import { AgentAvatar } from './AgentFigures'
import { agentLine } from './agentLine'
import { useOnScreen } from './useOnScreen'
import { agentLookFor, agentStateOf, modelOf, parsePreference } from './agentLooks'

export interface SceneLabels {
  containment: string
  waiting: (count: number) => string
  andMore: (count: number) => string
  /** What the card over a person says when no agent is reporting. Never "idle": see §43. */
  noAgent: string
  working: string
  allClear: string
  allClearHint: string
  readyForReview: string
  blocked: string
  testing: string
  /** A person is testing the fix, at their own bench. */
  underTest: string
  bugState: (state: BugState) => string
  activity: (state: ActivityState) => string
  /** "on <project>", for the accessible name of a person or clone the room labels with a project. */
  onProject?: (project: string) => string
  /** The words on the walls. Decoration, but translated decoration. */
  neon: [string, string, string]
  motto: [string, string, string, string]
  routineTitle: string
  routine: [string, string, string, string]
}

interface SceneProps {
  state: GameState
  labels: SceneLabels
  reducedMotion: boolean
  onSelectBug: (taskId: string | null) => void
  onSelectDeveloper: (developerId: string | null) => void
  onOpenContainment: () => void
  /** What each speaking character is saying right now, by developer id. */
  speech?: Map<string, string>
}

const INK = '#1a1f2c'

export const Scene = memo(function Scene({
  state,
  labels,
  reducedMotion,
  onSelectBug,
  onSelectDeveloper,
  onOpenContainment,
  speech,
}: SceneProps) {
  const developers = useMemo(() => [...state.developers.values()], [state.developers])

  /*
    The scene fills its container rather than being letterboxed inside it.

    A fixed viewBox inside a differently-shaped stage leaves empty bands and shrinks every figure
    to fit a height nothing is using. The width stays fixed so the layout arithmetic is stable; the
    height is measured, so the room is the shape of the space it was given.
  */
  const hostRef = useRef<SVGSVGElement>(null)
  // Off screen or in a hidden tab, the animations pause; with many agents, lines only for the selection (phase 4).
  const onScreen = useOnScreen(hostRef)
  const crowded = useMemo(() => isCrowded(state.developers.values()), [state.developers])
  const [size, setSize] = useState<FloorSize>({ width: DESIGN_WIDTH, height: 640 })

  // The stage around the drawing is measured, not the drawing: a big team's room is taller than the stage and
  // scrolls inside it, and measuring the drawing would chase its own height.
  const [stageWidth, setStageWidth] = useState(0)
  const [short, setShort] = useState(false)
  useEffect(() => {
    const host = hostRef.current?.parentElement
    if (!host) return

    const measure = (): void => {
      const rect = host.getBoundingClientRect()
      setStageWidth(rect.width)
      if (rect.width === 0) return

      const height = designHeightFor(rect.width, rect.height)
      setSize((current) => (current.height === height ? current : { width: DESIGN_WIDTH, height }))
      // Wider than the shortest room (a short window, the prompt box beside it): filling the width would crop the
      // floor where everybody stands, so the room shows whole, with bands at the sides.
      setShort(DESIGN_WIDTH * (rect.height / rect.width) < height)
    }

    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(host)
    return () => observer.disconnect()
  }, [])

  // A big team's room is deeper than the window (layout.ts, roomHeightFor): drawn at full size and the stage
  // scrolls down through it, instead of everybody shrunk onto each other's heads.
  const room = useMemo<FloorSize>(
    () => ({ width: size.width, height: roomHeightFor(developers.length, size) }),
    [developers.length, size],
  )
  const grown = room.height > size.height

  const floor = useMemo(
    () => computeFloor(developers.map((d) => d.id), room),
    [developers, room],
  )

  const tank = useMemo(() => containmentBox(room), [room])

  const stationById = useMemo(() => {
    const map = new Map<string, Station>()
    for (const station of floor.stations) map.set(station.developerId, station)
    return map
  }, [floor])

  // Bugs, sorted so the most urgent are placed first and end up in the nearest slots.
  const bugs = useMemo(
    () => [...state.bugs.values()].sort((a, b) => urgency(a.state, a.priority) - urgency(b.state, b.priority)),
    [state.bugs],
  )

  /*
    A bug goes to a station only if that station is on the floor.

    Asking whether the bug has a developer was not enough: an assigned bug whose developer was not
    on screen was drawn in the jar but not counted by it, so the jar showed a bug and said 0.
  */
  const visuals = useMemo(() => {
    const map = new Map<string, VisualBugState>()
    for (const bug of bugs) {
      const owner = ownerOf(bug)
      map.set(bug.taskId, visualStateFor(bug.state, Boolean(owner && stationById.has(owner))))
    }
    return map
  }, [bugs, stationById])

  const contained = bugs.filter((bug) => isContained(visuals.get(bug.taskId)!))
  const shownInTank = contained.slice(0, JAR_CELLS)
  const overflow = contained.length - shownInTank.length

  // At most eight bugs; cheaper to recompute than to memoise correctly.
  const cells = assignJarCells(shownInTank.map((bug) => seedFrom(bug.taskId)))
  const jarCells = new Map(shownInTank.map((bug, i) => [bug.taskId, cells[i]!]))

  // Each bug at a station gets its own slot there, so two bugs on one developer do not overlap.
  const stationSlots = useMemo(() => {
    const slots = new Map<string, number>()
    const perStation = new Map<string, number>()

    for (const bug of bugs) {
      const visual = visuals.get(bug.taskId)!
      const owner = ownerOf(bug)
      if (!owner || !(isAtStation(visual) || visual === 'walking')) continue

      const index = perStation.get(owner) ?? 0
      slots.set(bug.taskId, index)
      perStation.set(owner, index + 1)
    }

    return { slots, hunters: new Set(perStation.keys()) }
  }, [bugs, visuals])

  const quiet = bugs.length === 0
  const scenery = useScenery()

  return (
    <svg
      ref={hostRef}
      className={`bh-scene scenery--${scenery.time}${reducedMotion || !onScreen ? ' bh-scene--still' : ''}${grown ? ' bh-scene--grown' : ''}`}
      viewBox={`0 0 ${room.width} ${room.height}`}
      preserveAspectRatio={short && !grown ? 'xMidYMid meet' : 'xMidYMid slice'}
      style={grown && stageWidth > 0 ? { height: Math.round((stageWidth * room.height) / room.width) } : undefined}
      role="img"
      aria-label={labels.containment}
    >
      <SceneDefs />

      <Room size={room} labels={labels} />

      {/* Back to front, so a nearer station overlaps the one behind it. */}
      {floor.stations.map((station) => {
        const developer = state.developers.get(station.developerId)
        if (!developer) return null

        return (
          <StationGroup
            key={station.developerId}
            station={station}
            developer={developer}
            hunting={stationSlots.hunters.has(developer.id)}
            selected={state.selectedDeveloperId === developer.id}
            labels={labels}
            onSelect={onSelectDeveloper}
            speech={speech?.get(developer.id)}
            crowded={crowded}
          />
        )
      })}

      <Containment
        box={tank}
        count={contained.length}
        overflow={overflow}
        label={labels.containment}
        waitingLabel={labels.waiting(contained.length)}
        moreLabel={labels.andMore(overflow)}
        onClick={onOpenContainment}
      />

      {/* Bugs last, so one at a workstation reads as being in front of the desk. */}
      {bugs.map((bug) => {
        const visual = visuals.get(bug.taskId)!
        const owner = ownerOf(bug)
        const station = owner ? stationById.get(owner) : undefined
        const index = shownInTank.indexOf(bug)

        if (isContained(visual) && index === -1) return null

        return (
          <BugGroup
            key={bug.taskId}
            bug={bug}
            visual={visual}
            station={station}
            stationSlot={stationSlots.slots.get(bug.taskId) ?? 0}
            jarCell={jarCells.get(bug.taskId)}
            size={room}
            selected={state.selectedBugId === bug.taskId}
            labels={labels}
            onSelect={onSelectBug}
          />
        )
      })}

      {quiet && <AllClear box={tank} title={labels.allClear} hint={labels.allClearHint} />}
    </svg>
  )
})

/** Gradients and the neon's glow, shared by every scene drawn from these parts. */
export function SceneDefs() {
  return (
    <defs>
      <linearGradient id="bh-wall" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="var(--bh-wall-top)" />
        <stop offset="100%" stopColor="var(--bh-wall-bottom)" />
      </linearGradient>
      <linearGradient id="bh-floor" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="var(--bh-floor-far)" />
        <stop offset="100%" stopColor="var(--bh-floor-near)" />
      </linearGradient>
      <linearGradient id="bh-sky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="var(--bh-sky-top)" />
        <stop offset="100%" stopColor="var(--bh-sky-bottom)" />
      </linearGradient>
      <linearGradient id="bh-metal" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#C9D2DF" />
        <stop offset="100%" stopColor="#7C889B" />
      </linearGradient>
      <radialGradient id="bh-pool">
        <stop offset="0%" stopColor="rgba(255,214,150,0.13)" />
        <stop offset="100%" stopColor="rgba(255,214,150,0)" />
      </radialGradient>
      {/* The neon's glow: the shape blurred under itself. The only filter in the scene. */}
      <filter id="bh-glow" x="-20%" y="-40%" width="140%" height="180%">
        <feGaussianBlur stdDeviation="3.2" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  )
}

/* --------------------------------------------------------------------- room */

/**
 * The room: a back wall with windows onto a city at night, a neon sign, a chalkboard and a
 * whiteboard, and a floor with a rug and plants.
 *
 * Everything here is static and depends only on the size, so it renders once per resize.
 */
const Room = memo(function Room({ size, labels }: { size: FloorSize; labels: SceneLabels }) {
  const horizon = horizonFor(size)
  const { width, height } = size
  const { image, holiday } = useScenery()

  const windowHeight = horizon - 92

  return (
    <g aria-hidden="true">
      <rect x="0" y="0" width={width} height={horizon} fill="url(#bh-wall)" />

      {image ? (
        // A chosen picture fills the wall where the windows were; the boards stay in front of it.
        <>
          <image href={image} x="0" y="0" width={width} height={horizon} preserveAspectRatio="xMidYMid slice" />
          <rect className="bh-backdrop-dim" x="0" y="0" width={width} height={horizon} />
        </>
      ) : (
        /* Panelling: a few vertical seams, so the wall is a surface rather than a gradient. */
        <g stroke="rgba(255,255,255,0.035)" strokeWidth="2">
          {[180, 360, 640, 820].map((x) => (
            <line key={x} x1={x} y1="0" x2={x} y2={horizon} />
          ))}
        </g>
      )}

      {/* Laid out around what sits over the room: the counters down the left edge and the live
          indicator in the top-right corner. */}
      <Chalkboard x={126} y={44} width={118} height={Math.min(140, horizon - 100)} lines={labels.motto} />
      {!image && <CityWindow x={268} y={30} width={96} height={windowHeight} seed={3} />}
      {!image && <CityWindow x={636} y={30} width={96} height={windowHeight} seed={11} />}
      <RoutineBoard x={766} y={62} width={142} title={labels.routineTitle} items={labels.routine} />
      <NeonSign cx={width / 2} y={30} lines={labels.neon} />

      <rect x="0" y={horizon - 10} width={width} height="10" fill="var(--bh-skirting)" />
      <rect x="0" y={horizon} width={width} height={height - horizon} fill="url(#bh-floor)" />

      {/* Floor boards, receding. Depth without detail. */}
      <g stroke="var(--bh-line)" strokeWidth="1" opacity="0.45">
        {[0.12, 0.3, 0.55, 0.86].map((t) => (
          <line key={t} x1="0" y1={horizon + (height - horizon) * t} x2={width} y2={horizon + (height - horizon) * t} />
        ))}
        {Array.from({ length: 11 }, (_, i) => {
          const x = (width / 10) * i
          return <line key={i} x1={x} y1={height} x2={width / 2 + (x - width / 2) * 0.42} y2={horizon} />
        })}
      </g>

      {/* The rug the room is arranged around. */}
      <ellipse cx={width * 0.42} cy={horizon + (height - horizon) * 0.56} rx="300" ry="66" fill="var(--bh-rug)" stroke="var(--bh-rug-edge)" strokeWidth="3" />
      <ellipse cx={width * 0.42} cy={horizon + (height - horizon) * 0.56} rx="276" ry="56" fill="none" stroke="var(--bh-rug-edge)" strokeWidth="1.2" strokeDasharray="6 5" opacity="0.7" />

      {[0.22, 0.5, 0.78].map((t) => (
        <ellipse key={t} cx={width * t} cy={horizon + 110} rx="190" ry="95" fill="url(#bh-pool)" />
      ))}

      <Plant x={258} y={horizon + 12} scale={1.0} />
      <Plant x={744} y={horizon + 10} scale={0.9} />
      <Plant x={width + 4} y={height - 4} scale={1.2} />

      <HolidayDecor
        holiday={holiday}
        x={0}
        width={width}
        spots={[{ x: 190, y: horizon + 18 }, { x: 690, y: horizon + 16 }]}
      />
    </g>
  )
})

/** A window onto the city. Which windows are lit comes from a seed, so it is the same every time. */
export const CityWindow = memo(function CityWindow({
  x,
  y,
  width,
  height,
  seed,
}: {
  x: number
  y: number
  width: number
  height: number
  seed: number
}) {
  const { time, season } = useScenery()

  const buildings = useMemo(() => {
    const list: Array<{ x: number; w: number; h: number; far: boolean }> = []
    let cursor = 0
    let s = seed

    const next = (): number => {
      s = (s * 9301 + 49297) % 233280
      return s / 233280
    }

    while (cursor < width) {
      const w = 16 + next() * 22
      list.push({ x: cursor, w, h: height * (0.3 + next() * 0.5), far: next() > 0.55 })
      cursor += w + 2
    }

    return list
  }, [seed, width, height])

  return (
    <g>
      <rect x={x - 6} y={y - 6} width={width + 12} height={height + 12} rx="4" fill="var(--bh-window-frame)" stroke={INK} strokeWidth="1.5" />
      <svg x={x} y={y} width={width} height={height} viewBox={`0 0 ${width} ${height}`} overflow="hidden">
        <rect width={width} height={height} fill="url(#bh-sky)" />
        <Sky time={time} width={width} height={height} seed={seed} />

        {buildings.map((b, i) => (
          <g key={i}>
            <rect x={b.x} y={height - b.h} width={b.w} height={b.h} fill={b.far ? 'var(--bh-building-far)' : 'var(--bh-building-near)'} />
            {season === 'winter' && <rect x={b.x} y={height - b.h} width={b.w} height="2.4" fill="#F4F7FB" opacity="0.9" />}
            {!b.far &&
              Array.from({ length: Math.floor(b.h / 9) }, (_, row) =>
                Array.from({ length: Math.floor(b.w / 6) }, (_, col) => {
                  const lit = (i * 7 + row * 3 + col * 5 + seed) % 4 === 0
                  if (!lit) return null
                  return (
                    <rect key={`${row}-${col}`} className="bh-lit" x={b.x + 2 + col * 6} y={height - b.h + 4 + row * 9} width="2.6" height="3.6" fill="#F7C86B" />
                  )
                }),
              )}
          </g>
        ))}

        {season === 'winter' &&
          Array.from({ length: 9 }, (_, i) => (
            <circle
              key={i}
              className="bh-snowflake"
              style={{ animationDelay: `${(i * 0.83 + seed) % 7}s` }}
              cx={((i * 37 + seed * 13) % 100) / 100 * width}
              cy={4}
              r={i % 3 === 0 ? 1.6 : 1.1}
              fill="#FFFFFF"
            />
          ))}
        {season === 'autumn' &&
          Array.from({ length: 3 }, (_, i) => (
            <ellipse
              key={i}
              className="bh-leaf"
              style={{ animationDelay: `${i * 3 + (seed % 3)}s` }}
              cx={((i * 41 + seed * 7) % 90) / 100 * width + 6}
              cy={6}
              rx="2.6"
              ry="1.5"
              fill={i % 2 ? '#D9822B' : '#C2410C'}
            />
          ))}
      </svg>

      {/* Mullions and a sill. */}
      <line x1={x + width / 2} y1={y} x2={x + width / 2} y2={y + height} stroke="var(--bh-window-frame)" strokeWidth="4" />
      <line x1={x} y1={y + height * 0.58} x2={x + width} y2={y + height * 0.58} stroke="var(--bh-window-frame)" strokeWidth="4" />
      <rect x={x - 10} y={y + height + 4} width={width + 20} height="6" rx="2" fill="var(--bh-window-frame)" stroke={INK} strokeWidth="1.2" />
    </g>
  )
})

/**
 * What is in the sky at this hour: the sun on its way across, clouds by day, the moon and stars
 * at night. Placed from the window's seed too, so two windows side by side are not identical.
 */
function Sky({ time, width, height, seed }: { time: TimeOfDay; width: number; height: number; seed: number }) {
  const shift = ((seed * 17) % 20) / 100

  if (time === 'night') {
    return (
      <g>
        <circle cx={width * 0.78} cy={height * 0.18} r="7" fill="#F4F1DE" opacity="0.85" />
        {[0.1, 0.3, 0.5, 0.62, 0.9].map((t, i) => (
          <circle key={t} cx={width * t} cy={height * (0.08 + (i % 3) * 0.07)} r="0.9" fill="#FFFFFF" opacity="0.6" />
        ))}
      </g>
    )
  }

  const sun =
    time === 'dawn' ? { x: 0.22 + shift, y: 0.72, r: 11, color: '#FFC98A' }
      : time === 'day' ? { x: 0.7 + shift / 2, y: 0.18, r: 9, color: '#FFE066' }
        : time === 'afternoon' ? { x: 0.62 + shift / 2, y: 0.36, r: 9.5, color: '#FFD166' }
          : { x: 0.55 + shift, y: 0.7, r: 15, color: '#FF9E57' }

  return (
    <g>
      <circle cx={width * sun.x} cy={height * sun.y} r={sun.r * 2.2} fill={sun.color} opacity="0.22" />
      <circle cx={width * sun.x} cy={height * sun.y} r={sun.r} fill={sun.color} />
      {(time === 'day' || time === 'afternoon') && (
        <g className="bh-cloud" style={{ animationDelay: `${-seed * 3}s` }} fill="#FFFFFF" opacity="0.85">
          <ellipse cx={width * (0.25 + shift)} cy={height * 0.16} rx="11" ry="4.5" />
          <ellipse cx={width * (0.25 + shift) + 8} cy={height * 0.14} rx="7" ry="4.5" />
          {time === 'day' && <ellipse cx={width * (0.12 + shift)} cy={height * 0.3} rx="8" ry="3.4" />}
        </g>
      )}
      {time === 'dawn' && [0.12, 0.44, 0.8].map((t) => (
        <circle key={t} cx={width * t} cy={height * 0.1} r="0.8" fill="#FFFFFF" opacity="0.4" />
      ))}
    </g>
  )
}

/** A picture as shown: a GIF, for someone who asked for less motion, is its first frame, drawn once. */
function useStillPicture(source: string | null, still: boolean): string | null {
  const [frozen, setFrozen] = useState<string | null>(null)
  const gif = Boolean(source?.startsWith('data:image/gif'))
  useEffect(() => {
    setFrozen(null)
    if (!source || !still || !gif) return
    const image = new Image()
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = image.naturalWidth
        canvas.height = image.naturalHeight
        canvas.getContext('2d')?.drawImage(image, 0, 0)
        setFrozen(canvas.toDataURL('image/png'))
      } catch {
        // Could not draw it: the picture stays as it is.
      }
    }
    image.src = source
  }, [source, still, gif])
  return source && still && gif ? frozen : source
}

/**
 * The sign over the room. Its font size gives way to a long translation instead of overflowing.
 *
 * It is also the team's board (docs/refactor/25): a click sends a picture or a GIF, which covers the sign's square
 * without being distorted, for everyone in the room; another click replaces it. With reduced motion a GIF stays still.
 */
export const NeonSign = memo(function NeonSign({ cx, y, lines }: { cx: number; y: number; lines: [string, string, string] }) {
  const width = 250
  const longest = Math.max(...lines.map((line) => line.length))
  const fontSize = Math.min(22, (width - 36) / (longest * 0.68))
  const room = useSceneryChoice()
  const { t } = useI18n()
  const still = usePrefersReducedMotion()
  const board = useStillPicture(room?.board ?? null, still)
  const clip = useId().replace(/:/g, '')
  // What went wrong with a picture, under the sign for a few seconds (no dialog: the desktop's webview may not show one).
  const [problem, setProblem] = useState<string | null>(null)
  useEffect(() => {
    if (!problem) return
    const id = window.setTimeout(() => setProblem(null), 6000)
    return () => window.clearTimeout(id)
  }, [problem])
  const said = problem && (
    <text x={cx} y={y + 150} textAnchor="middle" className="bh-board__problem">{problem}</text>
  )
  // Back to the neon: a right click asks, a second one within a few seconds takes the board down.
  const [asking, setAsking] = useState(false)
  useEffect(() => {
    if (!asking) return
    const id = window.setTimeout(() => setAsking(false), 5000)
    return () => window.clearTimeout(id)
  }, [asking])
  const takeDown = (event: { preventDefault: () => void; stopPropagation: () => void }) => {
    event.preventDefault()
    event.stopPropagation()
    if (!room) return
    if (!asking) {
      setAsking(true)
      return
    }
    setAsking(false)
    room.removeBoard().catch(() => setProblem(t('room.board.removeFailed')))
  }

  const choose = (event: { stopPropagation: () => void }) => {
    event.stopPropagation()
    if (!room) return
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/png,image/jpeg,image/webp,image/gif'
    input.onchange = () => {
      const file = input.files?.[0]
      if (file) room.setBoard(file).catch(() => setProblem(t('room.board.failed')))
    }
    input.click()
  }
  const title = board ? t('room.board.change', { name: room?.boardBy ?? '' }) : t('room.board.add')

  if (board) {
    return (
      <g className="bh-board" role="button" tabIndex={0} aria-label={title} onClick={choose} onContextMenu={takeDown} onPointerDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') choose(event) }} style={{ cursor: 'pointer' }}>
        <title>{title}</title>
        <rect x={cx - width / 2 - 10} y={y - 8} width={width + 20} height="142" rx="10" fill="rgba(4,10,20,0.55)" />
        <clipPath id={clip}>
          <rect x={cx - width / 2} y={y} width={width} height="126" rx="12" />
        </clipPath>
        {/* Covering the square, cut rather than squeezed: "slice" keeps the picture's proportions. */}
        <image href={board} x={cx - width / 2} y={y} width={width} height="126" preserveAspectRatio="xMidYMid slice" clipPath={`url(#${clip})`} />
        <rect x={cx - width / 2} y={y} width={width} height="126" rx="12" fill="none" stroke="#39E58C" strokeWidth="3" />
        {asking ? <text x={cx} y={y + 150} textAnchor="middle" className="bh-board__ask">{t('room.board.removeAsk')}</text> : said}
      </g>
    )
  }

  return (
    <g className="bh-board" role={room ? 'button' : undefined} tabIndex={room ? 0 : undefined} aria-label={room ? title : undefined}
      onClick={room ? choose : undefined} onPointerDown={room ? (event) => event.stopPropagation() : undefined}
      onKeyDown={room ? (event) => { if (event.key === 'Enter' || event.key === ' ') choose(event) } : undefined}
      style={room ? { cursor: 'pointer' } : undefined}>
      {room && <title>{title}</title>}
      {said}
      <rect x={cx - width / 2 - 10} y={y - 8} width={width + 20} height="142" rx="10" fill="rgba(4,10,20,0.55)" />
      <g className="bh-neon" filter="url(#bh-glow)">
        <rect x={cx - width / 2} y={y} width={width} height="126" rx="12" fill="rgba(8,30,22,0.35)" stroke="#39E58C" strokeWidth="3" />
        {lines.map((line, i) => (
          <text
            key={i}
            x={cx}
            y={y + 34 + i * 27}
            textAnchor="middle"
            className="bh-neon__text"
            style={{ fontSize }}
          >
            {line.toUpperCase()}
          </text>
        ))}
        {/* The pulse from the logo, in neon. */}
        <path
          d={`M ${cx - 30} ${y + 108} h 16 l 6 -10 l 8 18 l 6 -8 h 24`}
          fill="none"
          stroke="#5BC8FF"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </g>
  )
})

export const Chalkboard = memo(function Chalkboard({
  x,
  y,
  width,
  height,
  lines,
}: {
  x: number
  y: number
  width: number
  height: number
  lines: [string, string, string, string]
}) {
  const step = (height - 30) / 4

  return (
    <g>
      <rect x={x} y={y} width={width} height={height} rx="5" fill="#6B4B35" stroke={INK} strokeWidth="1.5" />
      <rect x={x + 7} y={y + 7} width={width - 14} height={height - 14} rx="2" fill="#20312B" />
      <g className="bh-chalk" transform={`rotate(-2 ${x + width / 2} ${y + height / 2})`}>
        {lines.map((line, i) => (
          <text key={i} x={x + width / 2} y={y + 30 + i * step} textAnchor="middle">
            {line.toUpperCase()}
          </text>
        ))}
        <path
          d={`M ${x + 30} ${y + 36 + step} q 18 5 ${width - 60} 0`}
          fill="none"
          stroke="rgba(255,255,255,0.45)"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      </g>
      <rect x={x + 10} y={y + height - 4} width={width - 20} height="5" rx="2" fill="#5A3E2B" stroke={INK} strokeWidth="1" />
    </g>
  )
})

export const RoutineBoard = memo(function RoutineBoard({
  x,
  y,
  width,
  title,
  items,
}: {
  x: number
  y: number
  width: number
  title: string
  items: [string, string, string, string]
}) {
  return (
    <g>
      <rect x={x} y={y} width={width} height="126" rx="5" fill="#E9EEF6" stroke="#8B97AA" strokeWidth="3" />
      <text x={x + width / 2} y={y + 22} textAnchor="middle" className="bh-board__title">
        {title.toUpperCase()}
      </text>
      {items.map((item, i) => (
        <g key={i} transform={`translate(${x + 14} ${y + 44 + i * 21})`}>
          <rect x="0" y="-8" width="11" height="11" rx="2" fill="#FFFFFF" stroke="#5B677A" strokeWidth="1.2" />
          <path d="M 2 -2.5 L 4.6 0.5 L 10 -6.5" fill="none" stroke="#17A673" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <text x="18" y="1" className="bh-board__item">{item}</text>
        </g>
      ))}
    </g>
  )
})

/** A potted plant, its pot's base at the origin. */
export const Plant = memo(function Plant({ x, y, scale }: { x: number; y: number; scale: number }) {
  const leaves: Array<[number, number, string]> = [
    [-52, 1.0, 'var(--bh-plant)'],
    [-24, 1.18, 'var(--bh-plant-light)'],
    [4, 1.28, 'var(--bh-plant)'],
    [30, 1.12, 'var(--bh-plant-light)'],
    [56, 0.95, 'var(--bh-plant)'],
    [-8, 0.9, 'var(--bh-plant-light)'],
  ]

  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <ellipse cx="0" cy="0" rx="22" ry="5" fill="rgba(0,0,0,0.3)" />
      {leaves.map(([angle, length, color], i) => (
        <g key={i} transform={`translate(0 -30) rotate(${angle}) scale(${length})`}>
          <path d="M 0 0 C 10 -10 10 -32 0 -44 C -10 -32 -10 -10 0 0 Z" fill={color} stroke={INK} strokeWidth="1.2" />
          <path d="M 0 -2 L 0 -38" stroke="rgba(0,0,0,0.25)" strokeWidth="1.2" />
        </g>
      ))}
      <path d="M -17 -32 L 17 -32 L 13 0 L -13 0 Z" fill="var(--bh-pot)" stroke={INK} strokeWidth="1.5" strokeLinejoin="round" />
      <rect x="-19" y="-36" width="38" height="7" rx="2" fill="var(--bh-pot)" stroke={INK} strokeWidth="1.5" />
    </g>
  )
})

/* ------------------------------------------------------------------ station */

export const StationGroup = memo(function StationGroup({
  station,
  developer,
  hunting,
  selected,
  labels,
  onSelect,
  marked = false,
  detail,
  showPrompts,
  speech,
  speechScale = 1,
  project,
  projectOf,
  primarySessionId,
  crowded = false,
}: {
  station: Station
  developer: DeveloperEntity
  /** A bug is at this station, so the person is after it rather than just working. */
  hunting: boolean
  selected: boolean
  labels: SceneLabels
  onSelect: (id: string | null) => void
  /** Starred by the viewer, to follow. */
  marked?: boolean
  /** What the person's agent is on, in a few words — used by the room in place of the bare state. */
  detail?: string | null
  /** Prompts sent in the live session, drawn as a speech bubble beside the card. */
  showPrompts?: number
  /** What the person just said in the room's chat, over their head while it lasts. */
  speech?: string
  /** How much larger the bubble is drawn, so it stays readable when the camera is zoomed out. */
  speechScale?: number
  /**
   * The project the person's main agent is on, drawn over their card. The room passes it when no
   * project is filtered, so a floor of several projects says who is on which; absent, nothing is drawn.
   */
  project?: string | null
  /** The project each clone's agent is on, drawn over the clone. Given together with `project`. */
  projectOf?: (session: BugHuntSession) => string | null
  /**
   * The session the card describes. With it, every other busy session is a clone — so a person on
   * two projects at once shows both, each labelled with its own. Without it (the Bug Hunt), the
   * first session is the person and the busy ones after it are the clones, as before.
   */
  primarySessionId?: string
  /** A crowded room (layout.ts, CROWDED_AGENTS): the lines to this person's agents only while they are selected. */
  crowded?: boolean
}) {
  const look = useMemo(() => lookFor(developer.id, developer.avatar), [developer.id, developer.avatar])
  // How this person's AI agents look (docs/refactor/25): their preference, each agent seeded by its own id.
  const preference = useMemo(() => parsePreference(developer.agentLooks), [developer.agentLooks])

  const sessions = developer.sessions
  const working = sessions.length > 0 && developer.state !== 'Idle' && developer.state !== 'Completed'
  const primary = (primarySessionId ? sessions.find((session) => session.workSessionId === primarySessionId) : undefined) ?? sessions[0]
  // Which agent and model are behind the card's session (the owner's ask, 15/10).
  const agent = primary ? agentLine(primary) : null
  const busySessions = sessions.filter((session) => session.activityState !== 'Idle')
  const clones = primarySessionId
    ? busySessions.filter((session) => session.workSessionId !== primary?.workSessionId)
    : busySessions.slice(1)

  // A swing is drawn standing with the racket, even when the session that earned it just ended.
  const pose: Pose = developer.finishing ? 'hunting' : !working ? 'resting' : hunting ? 'hunting' : 'working'

  // Standing figures stand in front of their desk; a seated one sits at it.
  const figureX = pose === 'resting' ? 0 : 12
  const companion = agentSlotAt({ ...station, x: 0, y: 0, scale: 1 }, 0)
  // An agent with a character of its own is drawn as an agent, never as a person (docs/refactor/25).
  const selfLook = developer.isAgent ? agentLookFor(developer.id, preference) : null
  const agentTitle = (sessionId: string, name: string) => {
    const agentLook = agentLookFor(sessionId, preference)
    return `${name} · ${modelOf(agentLook).name}`
  }

  return (
    <g
      className={`bh-station${selected ? ' bh-station--selected' : ''}`}
      data-drop-developer={developer.id}
      transform={`translate(${station.x} ${station.y}) scale(${station.scale})`}
      onClick={(event) => {
        event.stopPropagation()
        onSelect(selected ? null : developer.id)
      }}
      tabIndex={0}
      role="button"
      aria-label={`${developer.name}: ${working ? labels.working : labels.noAgent}${agent ? ` · ${agent}` : ''}${project && labels.onProject ? ` · ${labels.onProject(project)}` : ''}`}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSelect(selected ? null : developer.id)
        }
      }}
    >
      {selected && <ellipse className="bh-station__ring" cx={figureX} cy="2" rx="46" ry="11" />}

      {/* The lines from the person to their agents: faint, and lit when the person is selected (docs/refactor/25). */}
      {!developer.isAgent && primary && (!crowded || selected) && (
        <path className={`bh-ag-link${selected ? ' bh-ag-link--on' : ''}`} d={`M ${figureX} -70 Q ${(figureX + companion.x) / 2} ${companion.y - 30} ${companion.x} ${companion.y - 4}`} />
      )}
      {!developer.isAgent && (!crowded || selected) && clones.slice(0, MAX_CLONES).map((session, index) => {
        const spot = cloneSpotAt(index)
        return <path key={`link-${session.workSessionId}`} className={`bh-ag-link${selected ? ' bh-ag-link--on' : ''}`} d={`M ${figureX} -70 Q ${(figureX + spot.x) / 2} -110 ${spot.x} -60`} />
      })}

      {selfLook ? (
        <>
          <g transform="translate(-42 -46) scale(0.8)">
            <Desk active={working} laptop={undefined} />
          </g>
          <g transform="translate(12 -52) scale(2.3)">
            <AgentAvatar look={selfLook} state={agentStateOf(working ? primary : primary ? { ...primary, activityState: 'Idle' } : null)} title={`${developer.name} · ${modelOf(selfLook).name}`} />
          </g>
        </>
      ) : pose === 'resting' ? (
        <>
          <Chair />
          <DeveloperFigure look={look} pose="resting" swinging={false} />
          <Desk active={false} laptop={look.laptop} />
        </>
      ) : (
        <>
          {/* The chair they got up from, pushed out, and the desk behind them. */}
          <g transform="translate(-96 -40) rotate(-10) scale(0.78)">
            <Chair />
          </g>
          {/* Hunting, they put the notebook down on the desk; working, it is in their hands. */}
          <g transform="translate(-42 -46) scale(0.8)">
            <Desk active laptop={pose === 'hunting' ? look.laptop : undefined} />
          </g>
          <g transform={`translate(${figureX} 0)`}>
            <DeveloperFigure look={look} pose={pose} swinging={developer.finishing} />
          </g>
        </>
      )}

      {/* The agent the person is working with, beside them. */}
      {primary && !developer.isAgent && (
        <g transform={`translate(${companion.x} ${companion.y}) scale(1.15)`} className="bh-companion" data-agent-session={primary.workSessionId}>
          <AgentAvatar look={agentLookFor(primary.workSessionId, preference)} state={agentStateOf(primary)} title={agentTitle(primary.workSessionId, developer.name)} />
        </g>
      )}

      {/* Each agent working past the first is a clone of the person, named for its number: 2_Alice,
          3_Alice. Quiet sessions do not count: a clone means two agents at work right now. Past
          MAX_CLONES the rest become a count. */}
      {!developer.isAgent && clones.slice(0, MAX_CLONES).map((session, index) => {
        const spot = cloneSpotAt(index)
        const cloneLook = agentLookFor(session.workSessionId, preference)
        // Each clone carries its own agent's project: two agents of one person on two projects are
        // two labels, not one person with the first project's name.
        // The project only when it is not the person's own: three agents on one project are not three labels.
        const ownProject = projectOf ? projectOf(session) : null
        const cloneProject = ownProject && (!primary || !projectOf || projectOf(primary) !== ownProject) ? ownProject : null
        const title = cloneProject && labels.onProject
          ? `${cloneName(index + 2, developer.name)} · ${labels.onProject(cloneProject)}`
          : cloneName(index + 2, developer.name)
        return (
          <g key={session.workSessionId} className="bh-clone" transform={`translate(${spot.x} ${spot.y})`} data-agent-session={session.workSessionId}>
            <title>{title}</title>
            {/* The agent itself, in the person's chosen look — not a copy of the person (docs/refactor/25). */}
            <g transform="translate(0 -30) scale(1.35)">
              <AgentAvatar look={cloneLook} state={agentStateOf(session)} />
            </g>
            {/* Its own place (phase 2b): robots and pets sit at a small desk of their own; an orb needs no desk and
                floats over its station, a soft glow under it. */}
            {cloneLook.family === 'orb' ? (
              <ellipse className="bh-orb-station" cx="0" cy="2" rx="16" ry="4" />
            ) : (
              <g className="bh-agent-desk" transform="translate(0 4) scale(0.4)">
                <Desk active={agentStateOf(session) === 'working'} />
              </g>
            )}
            {cloneProject && (
              <text className="bh-project bh-project--clone" x="0" y="-96" textAnchor="middle">
                {truncate(cloneProject, 20)}
              </text>
            )}
            <text className="bh-clone__name" x="0" y="-82" textAnchor="middle">
              {cloneName(index + 2, developer.name)}
            </text>
          </g>
        )
      })}

      {clones.length > MAX_CLONES && (
        <text className="bh-more-agents" x={cloneSpotAt(MAX_CLONES).x} y="-60" textAnchor="middle">
          +{clones.length - MAX_CLONES}
        </text>
      )}

      {/* The card over the person, drawn at a fixed size whatever the station's scale: a name that
          shrinks with the grid stops being readable exactly when the floor is busiest. */}
      <g transform={`translate(${figureX} ${cardY(pose, station.scale)}) scale(${station.cardScale / station.scale})`}>
        <StatusCard
          name={developer.name}
          working={working}
          // A session that has gone quiet is still a session: "agent quiet", not "no agent reporting".
          stateLabel={detail ?? (primary ? labels.activity(working ? primary.activityState : 'Idle') : labels.noAgent)}
          progress={working && primary ? primary.progressPercent : null}
          selected={selected}
          marked={marked}
          isAgent={developer.isAgent}
          status={developer.statusPhrase}
          project={project}
          agent={agent}
        />

        {/* Prompts sent in the live session: someone working with an agent on something nobody
            declared still shows as working. A count, never the text. */}
        {showPrompts !== undefined && showPrompts > 0 && (
          <g className="bh-prompts" transform={`translate(${CARD_WIDTH / 2 + 6} -40)`}>
            <path d="M 0 -9 h 24 a 5 5 0 0 1 5 5 v 9 a 5 5 0 0 1 -5 5 h -15 l -5 5 v -5 h -4 a 5 5 0 0 1 -5 -5 v -9 a 5 5 0 0 1 5 -5 z" />
            <text x="12" y="1.5" textAnchor="middle">{showPrompts > 99 ? '99+' : showPrompts}</text>
          </g>
        )}

        {speech && <SpeechBubble text={speech} scale={speechScale} />}

        {/* Floating telemetry, newest nearest. Rises and fades in CSS. Held back while the person
            speaks: the bubble is where their words go, and the two would overlap. */}
        {!speech && developer.messages.map((message, index) => (
          <text
            key={message.id}
            className={`bh-float bh-float--${message.tone}`}
            x="0"
            y={(project ? -76 : -58) - index * 15}
            textAnchor="middle"
          >
            {truncate(message.text, 34)}
          </text>
        ))}
      </g>
    </g>
  )
})

/**
 * A line of the room's chat over the speaker's card: a white bubble with its tail on the card.
 * Text in SVG is text — whatever was typed is drawn as characters, never as markup.
 */
function SpeechBubble({ text, scale }: { text: string; scale: number }) {
  const lines = wrapSpeech(text)
  const longest = Math.max(...lines.map((line) => line.length))
  const width = Math.min(230, Math.max(64, longest * 6.4 + 26))
  const height = lines.length * 15 + 14
  const bottom = -54
  const top = bottom - height
  const r = 10
  const half = width / 2

  const outline = [
    `M ${-half + r} ${top}`,
    `H ${half - r}`,
    `A ${r} ${r} 0 0 1 ${half} ${top + r}`,
    `V ${bottom - r}`,
    `A ${r} ${r} 0 0 1 ${half - r} ${bottom}`,
    'H 8',
    `L 0 ${bottom + 8}`,
    `L -8 ${bottom}`,
    `H ${-half + r}`,
    `A ${r} ${r} 0 0 1 ${-half} ${bottom - r}`,
    `V ${top + r}`,
    `A ${r} ${r} 0 0 1 ${-half + r} ${top}`,
    'Z',
  ].join(' ')

  // Grown about the tail's tip, which stays on the card.
  const tip = bottom + 8

  return (
    // The grow is on an outer group: the entrance animation sets its own transform on the inner one.
    <g transform={scale === 1 ? undefined : `translate(0 ${tip}) scale(${scale}) translate(0 ${-tip})`}>
      <g className="bh-speech" role="note" aria-label={text}>
        <path d={outline} />
        <text x="0" y={top + 18} textAnchor="middle">
          {lines.map((line, index) => (
            <tspan key={index} x="0" dy={index === 0 ? 0 : 15}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    </g>
  )
}

/**
 * How far above the feet the card sits, in the station's own units.
 *
 * A hunting station has a bug in the air beside the head, and that bug's tag, so the card goes one
 * tag-height higher there. The lift is in screen units — divided by the scale — because the tag it
 * makes room for is drawn at a fixed size.
 */
const HUNT_CARD_LIFT = 26

function cardY(pose: Pose, scale: number): number {
  if (pose === 'resting') return -118
  if (pose === 'hunting') return -142 - HUNT_CARD_LIFT / scale
  return -142
}

/**
 * Name, what the agent is doing, and how far along — the three things a manager reads at a glance.
 *
 * Its bottom edge sits at the origin.
 */
function StatusCard({
  name,
  working,
  stateLabel,
  progress,
  selected,
  marked = false,
  isAgent = false,
  status = null,
  project = null,
  agent = null,
}: {
  name: string
  working: boolean
  stateLabel: string
  progress: number | null
  selected: boolean
  marked?: boolean
  /** An agent with a character of its own: said on the card, never left to the drawing alone. */
  isAgent?: boolean
  /** The line they wrote under their own name, when they wrote one. */
  status?: string | null
  /** The project their agent is on: a tab over the card's top edge. */
  project?: string | null
  /** Which agent and model are behind the session: "Claude Code · fable-5". */
  agent?: string | null
}) {
  const width = CARD_WIDTH

  // The card grows for a line somebody wrote themselves, rather than squeezing it in beside what
  // their agent is doing: the two say different things and are read at different moments.
  const said = status?.trim() ? truncate(status.trim(), 26) : null
  // The agent line grows the card the same way: one more thing read at a glance, never squeezed.
  const height = 46 + (said ? 14 : 0) + (agent ? 13 : 0)
  const top = -height

  return (
    <g className={`bh-card${selected ? ' bh-card--selected' : ''}${marked ? ' bh-card--marked' : ''}${isAgent ? ' bh-card--agent' : ''}`}>
      {project && <ProjectTab x={-width / 2 + 8} y={top} text={truncate(project, 22)} />}
      <rect x={-width / 2} y={top} width={width} height={height} rx="9" className="bh-card__box" />
      <circle cx={-width / 2 + 13} cy={top + 14} r="3.6" className={working ? 'bh-dot bh-dot--on' : 'bh-dot'} />
      {/* §5: an agent is never mistaken for a person, so it carries the word, not just a colour. */}
      {isAgent && <AgentChip x={width / 2 - 30} y={top + 6} />}
      <text x={-width / 2 + 22} y={top + 18} className="bh-card__name">{truncate(name, isAgent ? 12 : marked ? 15 : 17)}</text>
      {marked && (
        <path
          className="bh-card__star"
          transform={`translate(${width / 2 - 14} ${top + 13}) scale(0.62)`}
          d="M 0 -9 L 2.6 -2.8 L 9.3 -2.8 L 3.9 1.2 L 5.9 7.6 L 0 3.7 L -5.9 7.6 L -3.9 1.2 L -9.3 -2.8 L -2.6 -2.8 Z"
        />
      )}
      {said && <text x={-width / 2 + 11} y={top + 32} className="bh-card__said">{said}</text>}
      {agent && <text x={-width / 2 + 11} y={top + (said ? 45 : 31)} className="bh-card__agent">{truncate(agent, 28)}</text>}
      <text x={-width / 2 + 11} y="-14" className="bh-card__state">{truncate(stateLabel, progress === null ? 26 : 19)}</text>

      {progress !== null && (
        <>
          <text x={width / 2 - 11} y="-14" textAnchor="end" className="bh-card__pct">{Math.round(progress)}%</text>
          <rect x={-width / 2 + 11} y="-8.5" width={width - 22} height="4" rx="2" className="bh-card__track" />
          <rect x={-width / 2 + 11} y="-8.5" width={((width - 22) * Math.max(0, Math.min(100, progress))) / 100} height="4" rx="2" className="bh-card__fill" />
        </>
      )}
    </g>
  )
}

/**
 * The project over a card: a small tab on its top edge, like a folder's, so it reads as belonging to
 * the card under it rather than floating free. Text in SVG is text — drawn as characters, never markup.
 */
function ProjectTab({ x, y, text }: { x: number; y: number; text: string }) {
  const width = Math.min(CARD_WIDTH - 16, Math.max(36, text.length * 6.1 + 16))
  return (
    <g className="bh-project-tab" transform={`translate(${x} ${y - 15})`}>
      <rect width={width} height="19" rx="5" />
      <text x="8" y="11.5" className="bh-project">{text}</text>
    </g>
  )
}

/** The mark that says a character is an agent: on its card, wherever it stands. */
function AgentChip({ x, y }: { x: number; y: number }) {
  return (
    <g className="bh-agent-chip" transform={`translate(${x} ${y})`}>
      <rect width="26" height="13" rx="6.5" />
      <text x="13" y="9.6" textAnchor="middle">AI</text>
    </g>
  )
}

/* --------------------------------------------------------------- containment */

/** The jar the waiting bugs are kept in, with the sign that says so. */
export const Containment = memo(function Containment({
  box,
  count,
  overflow,
  label,
  waitingLabel,
  moreLabel,
  onClick,
}: {
  box: { x: number; y: number; width: number; height: number }
  count: number
  overflow: number
  label: string
  waitingLabel: string
  moreLabel: string
  onClick: () => void
}) {
  const { x, y, width: w, height: h } = box

  const body =
    `M ${x + 36} ${y + 40} C ${x + 36} ${y + 54} ${x} ${y + 52} ${x} ${y + 84} ` +
    `L ${x} ${y + h - 30} Q ${x} ${y + h} ${x + 30} ${y + h} L ${x + w - 30} ${y + h} ` +
    `Q ${x + w} ${y + h} ${x + w} ${y + h - 30} L ${x + w} ${y + 84} ` +
    `C ${x + w} ${y + 52} ${x + w - 36} ${y + 54} ${x + w - 36} ${y + 40} Z`

  return (
    <g
      className="bh-tank"
      data-drop-jar=""
      onClick={(event) => {
        event.stopPropagation()
        onClick()
      }}
      tabIndex={0}
      role="button"
      aria-label={waitingLabel}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onClick()
        }
      }}
    >
      <ellipse cx={x + w / 2} cy={y + h + 2} rx={w / 2 + 10} ry="12" fill="rgba(0,0,0,0.35)" />

      <path d={body} className="bh-tank__glass" />
      <path d={`M ${x + 16} ${y + 96} L ${x + 16} ${y + h - 40}`} className="bh-tank__shine" />
      <path d={`M ${x + 30} ${y + 70} q 10 -8 22 -8`} className="bh-tank__shine bh-tank__shine--thin" />

      {/* The lid. */}
      <rect x={x + 26} y={y + 14} width={w - 52} height="26" rx="5" fill="url(#bh-metal)" stroke={INK} strokeWidth="1.5" />
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <line key={i} x1={x + 38 + i * ((w - 76) / 6)} y1={y + 18} x2={x + 38 + i * ((w - 76) / 6)} y2={y + 36} stroke="rgba(0,0,0,0.22)" strokeWidth="1.4" />
      ))}

      {overflow > 0 && (
        <text x={x + w / 2} y={y + h - 14} className="bh-tank__overflow" textAnchor="middle">
          {moreLabel}
        </text>
      )}

      {/* The sign, hung from the lid on two strings, a little crooked. */}
      <g transform={`rotate(-3 ${x + w / 2} ${y - 16})`}>
        <line x1={x + w / 2 - 50} y1={y - 2} x2={x + w / 2 - 44} y2={y + 16} stroke="#B89A7A" strokeWidth="1.4" />
        <line x1={x + w / 2 + 50} y1={y - 2} x2={x + w / 2 + 44} y2={y + 16} stroke="#B89A7A" strokeWidth="1.4" />
        <rect x={x + w / 2 - 100} y={y - 38} width="200" height="36" rx="6" fill="#8A5A3B" stroke={INK} strokeWidth="1.6" />
        <path d={`M ${x + w / 2 - 80} ${y - 26} h 60 M ${x + w / 2 + 10} ${y - 12} h 64`} stroke="rgba(0,0,0,0.18)" strokeWidth="1.4" />
        <text x={x + w / 2 - 16} y={y - 15} textAnchor="middle" className="bh-sign__text">
          {truncate(label.toUpperCase(), 18)}
        </text>
        <circle cx={x + w / 2 + 78} cy={y - 20} r="13" fill="#E5484D" stroke={INK} strokeWidth="1.5" />
        <text x={x + w / 2 + 78} y={y - 16} textAnchor="middle" className="bh-sign__count">{count}</text>
      </g>
    </g>
  )
})

/* ---------------------------------------------------------------------- bug */

/** The colour of a status pill. The words carry the meaning; the colour only speeds it up. */
/**
 * Whose station a bug sits at: the developer working on it — or, while somebody is testing the fix,
 * the tester's, because that is who has it.
 */
function ownerOf(bug: BugEntity): string | null {
  return bug.state === 'InTesting' ? bug.testerDeveloperId ?? bug.developerId : bug.developerId
}

const STATE_COLOR: Partial<Record<BugState, string>> = {
  Registered: '#64748B',
  Waiting: '#64748B',
  Assigned: '#3B82F6',
  InProgress: '#E5484D',
  Testing: '#D97706',
  InTesting: '#7C4DFF',
  Blocked: '#9F1239',
  ReadyForReview: '#B7791F',
  Completed: '#16A34A',
  Reopened: '#DB2777',
  Cancelled: '#475569',
}

export const BugGroup = memo(function BugGroup({
  bug,
  visual,
  station,
  stationSlot,
  jarCell: cell,
  size,
  selected,
  labels,
  onSelect,
}: {
  bug: BugEntity
  visual: VisualBugState
  station: Station | undefined
  stationSlot: number
  /** The jar cell, for a bug that is in the jar. */
  jarCell: number | undefined
  size: FloorSize
  selected: boolean
  labels: SceneLabels
  onSelect: (id: string | null) => void
}) {
  const seed = useMemo(() => seedFrom(bug.taskId), [bug.taskId])

  // A walking bug is on its way to the station, so its destination is the station: the transition
  // carries it there. It used to keep its jar position until work started, and never walked.
  const spot = useMemo(() => {
    if ((isAtStation(visual) || visual === 'walking') && station) return bugSlotAt(station, stationSlot)
    if (visual === 'defeated') return { x: size.width + 140, y: 60 }
    if (visual === 'returning') return { x: -120, y: size.height * 0.4 }
    return jarCell(cell ?? seed % JAR_CELLS, seed, size)
  }, [visual, station, stationSlot, seed, cell, size])

  const still = visual === 'contained' || visual === 'awaitingReview' || visual === 'tested'
  const inJar = visual === 'contained'
  const scale = inJar ? 0.72 : (station?.scale ?? 1) * 0.82

  // Above the bug, and above its glass, barrier or scanner when there is one. The card over the
  // developer is lifted to leave room for it: see HUNT_CARD_LIFT. Below the bug, the tag covered
  // the developer it is beside.
  const enclosed = visual === 'awaitingReview' || visual === 'blocked' || visual === 'tested'
  const tagY = -(enclosed ? 40 : 36) * scale - 12

  return (
    <g
      className={`bh-bug-slot bh-bug-slot--${visual}${selected ? ' bh-bug-slot--selected' : ''}`}
      data-bug={bug.taskId}
      transform={`translate(${spot.x} ${spot.y})`}
      onClick={(event) => {
        event.stopPropagation()
        onSelect(selected ? null : bug.taskId)
      }}
      tabIndex={0}
      role="button"
      aria-label={`${bug.externalId} ${bug.title}: ${labels.bugState(bug.state)}`}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSelect(selected ? null : bug.taskId)
        }
      }}
    >
      {/* The delay belongs here, on the group that animates: animation-delay is not inherited, so
          on the outer group it did nothing and every bug in the jar drifted in lockstep. */}
      <g className="bh-bug-slot__drift" style={{ animationDelay: `-${(seed % 1700) / 1000}s` }}>
        <g transform={`scale(${scale})`}>
          <BugCreature priority={bug.priority} seed={seed} weakened={bug.progressPercent > 70} still={still} />

          {visual === 'awaitingReview' && <ReviewGlass label={labels.readyForReview} />}
          {visual === 'blocked' && <BlockedBarrier label={labels.blocked} />}
          {visual === 'tested' && <Scanner label={labels.testing} passed={bug.testsPassed} total={bug.testsTotal} />}
          {visual === 'underTest' && <TestBench label={labels.underTest} />}
        </g>
      </g>

      {inJar ? (
        <JarTag id={bug.externalId} />
      ) : (
        <BugTag id={bug.externalId} y={tagY} state={labels.bugState(bug.state)} color={STATE_COLOR[bug.state] ?? '#64748B'} selected={selected} />
      )}
    </g>
  )
})

/** Id and status over a bug on the floor, like a name badge. Fixed size, like the person cards. */
function BugTag({ id, y, state, color, selected }: { id: string; y: number; state: string; color: string; selected: boolean }) {
  const idWidth = id.length * 6.7 + 14
  const pillWidth = state.length * 5.4 + 14
  const width = idWidth + pillWidth + 6
  const left = -width / 2

  return (
    <g className={`bh-tag${selected ? ' bh-tag--selected' : ''}`} transform={`translate(0 ${y})`}>
      <rect x={left} y="-11" width={width} height="22" rx="7" className="bh-tag__box" />
      <text x={left + 8} y="4" className="bh-tag__id">{id}</text>
      <rect x={left + idWidth} y="-7" width={pillWidth} height="14" rx="7" fill={color} />
      <text x={left + idWidth + pillWidth / 2} y="3.5" textAnchor="middle" className="bh-tag__state">{state}</text>
    </g>
  )
}

/**
 * A bug on a tester's bench: held on a small stand while a magnifying glass sweeps over it.
 *
 * Deliberately nothing like the agent's scanner: a person is looking at this one, and the sweep is
 * the only thing moving — the bug is held still, not being fixed.
 */
function TestBench({ label }: { label: string }) {
  return (
    <g className="bh-bench" aria-label={label}>
      <rect x="-30" y="20" width="60" height="7" rx="3" className="bh-bench__table" />
      <path d="M -16 20 L -12 10 M 16 20 L 12 10" className="bh-bench__clamp" />
      <g className="bh-bench__lens">
        <circle cx="0" cy="-6" r="15" className="bh-bench__glass" />
        <circle cx="0" cy="-6" r="15" className="bh-bench__rim" />
        <path d="M 10.5 4.5 L 20 15" className="bh-bench__handle" />
      </g>
    </g>
  )
}

/** The small label on a bug in the jar: an id and nothing else, because the jar is the status. */
function JarTag({ id }: { id: string }) {
  const width = id.length * 5.6 + 10

  // Centred under the bug, so a bug near the jar's wall does not hang its label outside the glass.
  return (
    <g className="bh-jar-tag" transform={`translate(${-width / 2} 20)`}>
      <rect x="0" y="-7" width={width} height="14" rx="3" />
      <text x="5" y="3.2">{id}</text>
    </g>
  )
}

/** The glass that holds a bug awaiting review: stunned, not gone — a person still has to look. */
function ReviewGlass({ label }: { label: string }) {
  return (
    <g className="bh-glass" aria-label={label}>
      <path d="M -26 22 L -26 -6 C -26 -36 26 -36 26 -6 L 26 22 Z" className="bh-glass__dome" />
      <path d="M -18 14 L -18 -6 C -18 -18 -12 -24 -6 -26" className="bh-glass__shine" />
      <circle cx="0" cy="-30" r="4" fill="#C9D3E2" stroke={INK} strokeWidth="1.2" />
      <rect x="-31" y="21" width="62" height="6" rx="2" fill="#8B97AA" stroke={INK} strokeWidth="1.2" />
    </g>
  )
}

function BlockedBarrier({ label }: { label: string }) {
  return (
    <g className="bh-barrier" aria-label={label}>
      <circle cx="0" cy="-4" r="30" />
    </g>
  )
}

function Scanner({ label, passed, total }: { label: string; passed: number; total: number }) {
  return (
    <g className="bh-scanner" aria-label={label}>
      <rect x="-34" y="-34" width="68" height="60" rx="8" />
      <line className="bh-scanner__beam" x1="-30" y1="-4" x2="30" y2="-4" />
      {total > 0 && (
        <text x="0" y="40" textAnchor="middle" className="bh-scanner__count">
          {passed}/{total}
        </text>
      )}
    </g>
  )
}

/* -------------------------------------------------------------- empty state */

export function AllClear({
  box,
  title,
  hint,
}: {
  box: { x: number; y: number; width: number }
  title: string
  hint: string
}) {
  return (
    <g className="bh-clear" transform={`translate(${box.x + box.width / 2} ${box.y - 84})`}>
      <text y="0" textAnchor="middle" className="bh-clear__title">{title}</text>
      <text y="20" textAnchor="middle" className="bh-clear__hint">{hint}</text>
    </g>
  )
}

function truncate(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`
}
